import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { invitationsAPI } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Card, CardContent, CardDescription, CardHeader, CardTitle,
} from '../components/ui/card';
import { AlertCircle, UserPlus, CheckCircle2 } from 'lucide-react';

/**
 * AcceptInvite — public page that consumes an invitation token.
 *
 * URL: /invitations/accept?token=<token>
 *
 * Flow:
 *  1. On mount, GET /api/invitations/lookup?token=... to validate the
 *     token and show the invitee what they're joining.
 *  2. User sets name + password, submits POST /api/invitations/accept.
 *  3. Backend provisions the user and sets the auth cookie; we refresh
 *     the auth context and route to the right dashboard.
 */
export default function AcceptInvite() {
  const [params] = useSearchParams();
  const token = params.get('token') || '';
  const navigate = useNavigate();
  const { refreshUser } = useAuth();

  const [lookup, setLookup] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({ name: '', password: '', confirmPassword: '' });

  useEffect(() => {
    let cancelled = false;
    async function run() {
      if (!token) {
        setError('This invitation link is missing a token.');
        setLoading(false);
        return;
      }
      try {
        const res = await invitationsAPI.lookup(token);
        if (!cancelled) setLookup(res.data);
      } catch (e) {
        if (!cancelled) setError(e?.response?.data?.detail || 'Invitation is invalid or has expired.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    run();
    return () => { cancelled = true; };
  }, [token]);

  const handleChange = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!form.name.trim()) { setError('Please enter your name'); return; }
    if (form.password.length < 6) { setError('Password must be at least 6 characters'); return; }
    if (form.password !== form.confirmPassword) { setError('Passwords do not match'); return; }

    setSubmitting(true);
    try {
      const res = await invitationsAPI.accept({
        token,
        name: form.name.trim(),
        password: form.password,
      });
      // Pull the fresh session from /auth/me so the AuthContext has the right role.
      await refreshUser();
      const role = res.data?.user?.role;
      if (role === 'medic') navigate('/portal', { replace: true });
      else navigate('/dashboard', { replace: true });
    } catch (err) {
      setError(err?.response?.data?.detail || 'Failed to accept invitation');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#1A1A1A] flex items-center justify-center p-4">
        <div className="w-10 h-10 border-2 border-[#1ABC9C]/30 border-t-[#1ABC9C] rounded-full animate-spin" />
      </div>
    );
  }

  if (error && !lookup) {
    return (
      <div className="min-h-screen bg-[#1A1A1A] flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          <div className="text-center mb-8">
            <h1 className="text-3xl font-bold text-[#1ABC9C]">Elara</h1>
          </div>
          <Card className="bg-[#2B2B2B] border-white/5">
            <CardHeader>
              <CardTitle className="text-[#F2F2F2] flex items-center gap-2">
                <AlertCircle className="w-5 h-5 text-red-400" /> Invitation unavailable
              </CardTitle>
              <CardDescription className="text-[#C7C7C7]">{error}</CardDescription>
            </CardHeader>
            <CardContent>
              <Link
                to="/login"
                className="text-[#1ABC9C] hover:underline"
                data-testid="invite-login-link"
              >
                Go to sign in
              </Link>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  const roleLabel = (lookup?.role || '').replace('_', ' ');

  return (
    <div className="min-h-screen bg-[#1A1A1A] flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-[#1ABC9C]">Elara</h1>
          <p className="text-[#C7C7C7] mt-2">Workforce Management System</p>
        </div>

        <Card className="bg-[#2B2B2B] border-white/5">
          <CardHeader>
            <CardTitle
              className="text-[#F2F2F2] flex items-center gap-2"
              data-testid="invite-title"
            >
              <CheckCircle2 className="w-5 h-5 text-[#1ABC9C]" />
              Join {lookup?.organisation_name}
            </CardTitle>
            <CardDescription className="text-[#C7C7C7]" data-testid="invite-description">
              You've been invited by <strong>{lookup?.invited_by_name || 'your admin'}</strong>{' '}
              to join <strong>{lookup?.organisation_name}</strong> as a{' '}
              <span className="capitalize text-[#1ABC9C]">{roleLabel}</span>. Set your password
              to get started.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4" data-testid="invite-accept-form">
              {error && (
                <div
                  className="flex items-center gap-2 p-3 bg-red-500/10 border border-red-500/20 rounded-md text-red-400 text-sm"
                  data-testid="invite-error"
                >
                  <AlertCircle className="w-4 h-4" /> {error}
                </div>
              )}

              <div className="space-y-2">
                <Label className="text-[#F2F2F2]">Email</Label>
                <Input
                  value={lookup?.email || ''}
                  disabled
                  className="bg-[#1A1A1A] border-[#3D3D3D] text-[#C7C7C7]"
                  data-testid="invite-email-input"
                />
                <p className="text-xs text-[#A1A1AA]">
                  This email was set by your admin and cannot be changed here.
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="name" className="text-[#F2F2F2]">Your Name</Label>
                <Input
                  id="name"
                  type="text"
                  value={form.name}
                  onChange={(e) => handleChange('name', e.target.value)}
                  placeholder="Alex Morgan"
                  className="bg-[#1A1A1A] border-[#3D3D3D] text-[#F2F2F2] placeholder:text-[#C7C7C7]/50 focus:border-[#1ABC9C] focus:ring-[#1ABC9C]"
                  required
                  data-testid="invite-name-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="password" className="text-[#F2F2F2]">Password</Label>
                <Input
                  id="password"
                  type="password"
                  value={form.password}
                  onChange={(e) => handleChange('password', e.target.value)}
                  placeholder="Minimum 6 characters"
                  className="bg-[#1A1A1A] border-[#3D3D3D] text-[#F2F2F2] placeholder:text-[#C7C7C7]/50 focus:border-[#1ABC9C] focus:ring-[#1ABC9C]"
                  required
                  data-testid="invite-password-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="confirmPassword" className="text-[#F2F2F2]">Confirm Password</Label>
                <Input
                  id="confirmPassword"
                  type="password"
                  value={form.confirmPassword}
                  onChange={(e) => handleChange('confirmPassword', e.target.value)}
                  placeholder="Repeat your password"
                  className="bg-[#1A1A1A] border-[#3D3D3D] text-[#F2F2F2] placeholder:text-[#C7C7C7]/50 focus:border-[#1ABC9C] focus:ring-[#1ABC9C]"
                  required
                  data-testid="invite-confirm-password-input"
                />
              </div>

              <Button
                type="submit"
                className="w-full bg-[#1ABC9C] hover:bg-[#1ABC9C]/90 text-white"
                disabled={submitting}
                data-testid="invite-submit-btn"
              >
                {submitting ? (
                  <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                ) : (
                  <>
                    <UserPlus className="w-4 h-4 mr-2" />
                    Accept invitation
                  </>
                )}
              </Button>
            </form>

            <div className="mt-6 text-center text-sm text-[#C7C7C7]">
              Already have an account?{' '}
              <Link to="/login" className="text-[#1ABC9C] hover:underline" data-testid="invite-login-link">
                Sign in
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
